<template>
  <Builder
    v-if="!loading"
    v-model="toggleValue"
    :settings="settings"
  >
    <template #actions>
      <TemplateActions
        :loading="isLoading"
        @save="onSave"
      />
    </template>
  </Builder>
</template>

<script
  lang="ts"
  setup
>
import { useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { emailTemplateFragment } from '@/graphql-fragments';
import { flash, modal } from '@/helpers/ui';
import Builder from '@/modules/marketing/email/builder/index.vue';
import { useCompanyStore } from '@/stores/company';
import { useMarketingStore } from '../../../store';
import TemplateActions from '../../TemplateActions.vue';

const { t } = useI18n();
const isLoading = ref(false);
const router = useRouter();
const { updateCompany } = useCompanyStore();
const { isSubmitted, isInvalid } = storeToRefs(useMarketingStore());
const { setAttrs, updateEmailTemplate } = useMarketingStore();
const settings = {
  emailType: 'BIRTHDAY',
  title: t('marketing.email.automated_emails.birthday.title'),
  description: t('marketing.email.automated_emails.birthday.intro'),
  sentDescription: t('marketing.email.automated_emails.birthday.when'),
  receiveDescription: t(
    'marketing.email.automated_emails.birthday.receive_info',
  ),
  showToggle: true,
  allowImageUpload: false,
};

const toggleValue = computed({
  get() {
    return (
      useCompanyStore().companySettings.customers?.birthdayMessageEnabled ||
      false
    );
  },
  set(value) {
    const input = {
      settings: {
        customers: {
          birthdayMessageEnabled: value,
        },
      },
    };

    updateCompany(input).then(() => {
      flash(
        t(`global.flash.birthday_message_${value ? 'enabled' : 'disabled'}`),
      );
    });
  },
});

const onSave = () => {
  isSubmitted.value = true;

  if (isInvalid.value) {
    return;
  }

  modal('confirmation').then(() => {
    isLoading.value = true;
    updateEmailTemplate('BIRTHDAY').finally(() => {
      router.push({ name: 'marketing-email' });
      flash(t('global.flash.email_saved'));
      isSubmitted.value = false;
      isLoading.value = false;
    });
  });
};

const { result, onResult, loading } = useQuery(
  gql`
    query getEmailTemplate($emailType: EmailTemplateEnum!) {
      emailTemplate(emailType: $emailType) {
        ...emailTemplate
      }
      emailTemplateDefault: emailTemplate(
        emailType: $emailType
        defaultValues: true
      ) {
        ...emailTemplate
      }
    }
    ${emailTemplateFragment}
  `,
  {
    emailType: 'BIRTHDAY',
  },
);

onResult(() => {
  if (result.value.emailTemplate) {
    result.value.emailTemplate.emailTemplateDefault =
      result.value.emailTemplateDefault;
    setAttrs(result.value.emailTemplate);
  }
});
</script>
